import { yupResolver } from '@hookform/resolvers/yup'
import { TextField } from '@mui/material'
import classNames from 'classnames'
import { ScreenEnum, useScreen } from 'feature/screenMode'
import { messages } from 'helpers/validationMessages'
import { forwardRef, useEffect, useState } from 'react'
import { useForm, Controller, FormProvider } from 'react-hook-form'
import { useActions, useAppSelector } from 'store/hooks'
import { Button, MenuItem } from 'ui'
import { SelectMultipleDropdown } from 'ui/SelectMultiple'
import { DragDropInput } from 'widget/DragDropFile/dragDropInput/DragDropInput'
import { LoaderInCentalPosition } from 'widget/LoaderInCentalPosition/LoaderInCentalPosition'
import { ModalCloseButton } from 'widget/ModalCloseButton/ModalCloseButton'
import * as yup from 'yup'

import style from './ModalOrderCall.module.scss'
import { TModalOrderCall } from './types'

// модальное окно для консультаций общее
// eslint-disable-next-line react/display-name
export const ModalOrderCall = forwardRef<HTMLDivElement, TModalOrderCall>(
  (
    {
      close,
      onSubmit,
      selectFiled,
      title,
      btnDisabled,
      btnLoading,
      description,
      fileList,
      loadFile,
      setFileList,
      SubmitButtonIcon,
      submitButtonText,
    },
    ref,
  ) => {
    const [isButtonDisabled, setButtonDisabled] = useState<boolean>(false)
    const [imageInit, setImageInit] = useState(false)

    const currentEditConsultForm = useAppSelector(
      state => state.orderForm.currentEditConsultForm,
    )
    const ConsultForm = useAppSelector(
      state => state.orderForm.consultationForms[currentEditConsultForm],
    )

    const consultProduct = useAppSelector(
      state => state.orderForm.consultationForms.consultationProduct.products,
    )

    const {
      setConsultationComment,
      setConsultationImages,
      setConsultationProducts,
    } = useActions()

    const schema = yup.object().shape({
      type: yup.lazy(() =>
        selectFiled?.selectProps.required
          ? yup.array().of(yup.string()).required(messages.required)
          : yup.array().of(yup.string()),
      ),
      files: yup.object().shape({}),
      comment: yup
        .string()
        .required(messages.required)
        .max(250, messages.maxLength(250))
        .test('notOnlySpaces', messages.required, value => value.trim() !== ''),
    })

    const formMethods = useForm({
      defaultValues: {
        comment: ConsultForm.comment,
      },
      resolver: yupResolver(schema),
      mode: 'all',
    })

    const { handleSubmit, control, clearErrors, setValue } = formMethods

    const screen = useScreen()

    const handleProductsChange = (selectedNames: string[]) => {
      setConsultationProducts(
        selectFiled?.selectList
          .filter(item => selectedNames.includes(item.value))
          .map(item => ({ id: item.id, name: item.value })) ?? [],
      )
    }

    const selectItems = selectFiled?.selectList.map(item => {
      if (item.hasChildren)
        return (
          <span key={item.id} className={style.subHeader}>
            {item.value}
          </span>
        )

      return (
        <MenuItem value={item.value} key={item.id} className={style.menuItem}>
          <span
            className={classNames(style.childItem, {
              [style.parentItem]: !item.isChild,
            })}
          >
            {item.value}
          </span>
        </MenuItem>
      )
    })

    useEffect(() => {
      if (imageInit) setConsultationImages(fileList)
    }, [fileList])

    useEffect(() => {
      setFileList(ConsultForm.images)
      setImageInit(true)
    }, [])

    useEffect(() => {
      if (ConsultForm.comment.length) {
        setButtonDisabled(false)
        clearErrors('comment')
        setValue('comment', ConsultForm.comment)
      } else setButtonDisabled(true)
    }, [ConsultForm.comment])

    return (
      <div ref={ref} className={style.modal}>
        <h1>{title}</h1>
        <span className={classNames(style.subtitle, 'default-text ')}>
          {description}
        </span>
        <ModalCloseButton onClose={close} />
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {selectFiled && (
              <Controller
                name='type'
                control={control}
                defaultValue={[]}
                render={({ field, fieldState }) => (
                  <SelectMultipleDropdown
                    selectProps={{
                      error: !!fieldState?.error,
                      ...field,
                      ...selectFiled.selectProps,
                    }}
                    helperText={fieldState?.error?.message}
                    formControlProps={{
                      sx: { minWidth: '100%' },
                      error: !!fieldState.error?.message?.length,
                    }}
                    value={consultProduct?.map(p => p.name) ?? []}
                    onChange={handleProductsChange}
                  >
                    {selectItems}
                  </SelectMultipleDropdown>
                )}
              />
            )}

            <Controller
              name='comment'
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  label='Комментарий'
                  required
                  multiline
                  inputProps={{ maxLength: 250, autoComplete: 'off' }}
                  error={!!fieldState?.error}
                  helperText={fieldState?.error?.message}
                  data-testid='consultation-comment'
                  {...field}
                  value={ConsultForm.comment}
                  onChange={e => setConsultationComment(e.target.value)}
                />
              )}
            />
            <Controller
              name='files'
              control={control}
              render={() => (
                <DragDropInput
                  loadFileToApi={loadFile}
                  limit={5}
                  errorText='1 файл не загружен - превышен лимит'
                  helperTitle='Загрузите файл'
                  allowedFormats='image/png, image/jpeg, image/jpg'
                  fileList={fileList}
                  setFileList={setFileList}
                  isError={false}
                />
              )}
            />
            <div className={style.buttonWrapper}>
              <Button
                color='primary'
                variant='contained'
                disabled={isButtonDisabled || btnLoading || btnDisabled}
                size='large'
                type='submit'
                className={style.submitButton}
                startIcon={SubmitButtonIcon}
                data-testid='consultation-submit'
              >
                {btnLoading && (
                  <LoaderInCentalPosition className={style.loader} />
                )}
                {submitButtonText}
              </Button>
              {screen === ScreenEnum.mobile && (
                <Button
                  color='primary'
                  variant='text'
                  size='large'
                  onClick={close}
                  className={style.backButton}
                  data-testid='consultation-back'
                >
                  Назад
                </Button>
              )}
            </div>
          </form>
        </FormProvider>
      </div>
    )
  },
)
